'use client';
import { forwardRef } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { ChevronDown, Check } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils/cn';

const selectVariants = cva(
  'relative w-full cursor-pointer rounded-md border border-gray-300 bg-white px-3 py-2 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default: '',
        error: 'border-red-500 focus:ring-red-500',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface SelectProps extends VariantProps<typeof selectVariants> {
  options: SelectOption[];
  value: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = 'Select an option',
      className,
      variant,
      disabled,
      ...props
    },
    ref
  ) => {
    const selectedOption = options.find(option => option.value === value);

    const handleChange = (option: SelectOption) => {
      onChange(option.value);
    };

    return (
      <Listbox
        value={selectedOption}
        onChange={handleChange}
        disabled={disabled}
        {...props}
      >
        <div className='relative'>
          <ListboxButton
            ref={ref}
            className={cn(selectVariants({ variant, className }))}
          >
            <span
              className={`block truncate ${selectedOption ? 'text-brand-navy' : 'text-gray-500'}`}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <span className='absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none'>
              <ChevronDown
                className='w-4 h-4 text-gray-400'
                aria-hidden='true'
              />
            </span>
          </ListboxButton>
          <Transition
            as='div'
            leave='transition ease-in duration-100'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <ListboxOptions className='absolute z-50 w-full py-1 mt-1 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg max-h-60'>
              {options.map(option => (
                // @ts-ignore
                <ListboxOption
                  key={option.value}
                  value={option}
                  className='relative px-3 py-2 text-sm text-gray-900 cursor-pointer select-none hover:bg-brand-teal/10 hover:text-brand-navy'
                >
                  {({ selected }) => (
                    <div className='flex items-center justify-between'>
                      <span
                        className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
                      >
                        {option.label}
                      </span>
                      {selected && (
                        <Check
                          className='w-4 h-4 text-brand-teal'
                          aria-hidden='true'
                        />
                      )}
                    </div>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      </Listbox>
    );
  }
);
Select.displayName = 'Select';

export { Select };
